import { useState, useEffect, useCallback } from 'react';
import * as DB from '@/lib/supabaseDb';
import { useNavigate } from 'react-router-dom';
import { formatTRY } from '@/lib/currencyFormat';
import Badge from '@/components/base/Badge';
import { promiseRecordStatus } from '@/lib/statusDefs';

interface CustomerPaymentPromisesTabProps {
  customerId: string;
  refreshVer: number;
}

export default function CustomerPaymentPromisesTab({ customerId, refreshVer }: CustomerPaymentPromisesTabProps) {
  const navigate = useNavigate();
  const [promises, setPromises] = useState<any[]>([]);
  const [activities, setActivities] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPromise, setSelectedPromise] = useState<any | null>(null);

  const loadData = useCallback(async () => {
    if (!customerId) return;
    setLoading(true);
    try {
      const [promRes, actRes] = await Promise.all([
        DB.getPaymentPromisesByCustomerId(customerId),
        DB.getPaymentPromiseActivitiesByCustomerId(customerId),
      ]);
      setPromises((promRes.data as any[]) || []);
      setActivities((actRes.data as any[]) || []);
    } catch {
      // Silently handle
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => { loadData(); }, [loadData, refreshVer]);

  const contactMethodIcons: Record<string, string> = {
    'Telefon': 'ri-phone-line',
    'WhatsApp': 'ri-whatsapp-line',
    'Yüz Yüze': 'ri-user-voice-line',
    'Diğer': 'ri-message-2-line',
  };

  const activityTypeLabels: Record<string, string> = {
    'Ertelendi': 'Ertelendi',
    'Düzenlendi': 'Düzenlendi',
    'Görüşme': 'Görüşme',
    'Ödeme Alındı': 'Ödeme Alındı',
  };

  const activityTypeIcons: Record<string, string> = {
    'Ertelendi': 'ri-calendar-schedule-line',
    'Düzenlendi': 'ri-edit-line',
    'Görüşme': 'ri-chat-3-line',
    'Ödeme Alındı': 'ri-bank-card-line',
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="w-8 h-8 rounded-full border-2 border-primary-200 border-t-primary-500 animate-spin"></div>
      </div>
    );
  }

  if (promises.length === 0) {
    return (
      <div className="p-8 flex flex-col items-center gap-3">
        <div className="w-14 h-14 rounded-2xl bg-background-100 flex items-center justify-center">
          <i className="ri-hand-heart-line text-2xl text-foreground-300"></i>
        </div>
        <p className="text-sm text-foreground-400">Bu müşteri için henüz ödeme sözü kaydı bulunmuyor.</p>
        <button onClick={() => navigate('/payment-promises')} className="btn-outline text-sm cursor-pointer">
          <i className="ri-add-line mr-1.5"></i>Yeni Ödeme Sözü Ekle
        </button>
      </div>
    );
  }

  const activePromises = promises.filter(p => p.status !== 'Ödeme Alındı' && p.status !== 'İptal');
  const completedPromises = promises.filter(p => p.status === 'Ödeme Alındı' || p.status === 'İptal');
  const filteredActivities = selectedPromise
    ? activities.filter(a => a.payment_promise_id === selectedPromise.id)
    : activities;

  return (
    <div className="p-4 lg:p-6 space-y-6">
      {/* Aktif Sözler */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-semibold text-foreground-800">Aktif Ödeme Sözleri ({activePromises.length})</h4>
        </div>
        {activePromises.length === 0 ? (
          <p className="text-sm text-foreground-400">Aktif ödeme sözü bulunmuyor.</p>
        ) : (
          <div className="space-y-2">
            {activePromises.map((promise) => {
              const todayStr = new Date().toLocaleDateString('en-CA', { timeZone: 'Europe/Istanbul' });
              const isOverdue = promise.promised_date < todayStr &&
                ['Bekliyor', 'Bugün Ödenecek', 'Ertelendi'].includes(promise.status);
              return (
                <div
                  key={promise.id}
                  onClick={() => setSelectedPromise(selectedPromise?.id === promise.id ? null : promise)}
                  className={`bg-background-50 rounded-xl border p-3.5 cursor-pointer transition-colors ${isOverdue ? 'border-red-300 bg-red-50/30' : 'border-background-200/70 hover:bg-background-100/50'} ${selectedPromise?.id === promise.id ? 'ring-2 ring-primary-200' : ''}`}
                >
                  <div className="flex items-start justify-between gap-2 flex-wrap">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground-800">{formatTRY(promise.promised_amount || 0)}</p>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <span className={`text-xs font-medium ${isOverdue ? 'text-red-600' : 'text-foreground-500'}`}>
                          <i className="ri-calendar-line mr-0.5"></i>
                          {promise.promised_date ? new Date(promise.promised_date).toLocaleDateString('tr-TR') : '—'}
                        </span>
                        <span className="text-xs text-foreground-400">
                          <i className={`${contactMethodIcons[promise.contact_method] || 'ri-phone-line'} mr-0.5`}></i>
                          {promise.contact_method}
                        </span>
                        {promise.paid_amount > 0 && (
                          <span className="text-xs text-emerald-600">Ödenen: {formatTRY(promise.paid_amount)}</span>
                        )}
                      </div>
                      {promise.staff_note && (
                        <p className="text-xs text-foreground-400 mt-1.5 truncate max-w-md">{promise.staff_note}</p>
                      )}
                    </div>
                    <Badge badgeClass={promiseRecordStatus.badgeClass(promise.status)} icon={promiseRecordStatus.icon(promise.status)}>{promise.status}</Badge>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Geçmiş Sözler */}
      {completedPromises.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-foreground-800 mb-3">Geçmiş Ödeme Sözleri ({completedPromises.length})</h4>
          <div className="space-y-2">
            {completedPromises.map((promise) => (
              <div
                key={promise.id}
                onClick={() => setSelectedPromise(selectedPromise?.id === promise.id ? null : promise)}
                className={`bg-background-50 rounded-xl border border-background-200/70 p-3 cursor-pointer transition-colors hover:bg-background-100/50 ${selectedPromise?.id === promise.id ? 'ring-2 ring-primary-200' : ''}`}
              >
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground-700">{formatTRY(promise.promised_amount || 0)}</p>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <span className="text-xs text-foreground-400">
                        <i className="ri-calendar-line mr-0.5"></i>
                        {promise.promised_date ? new Date(promise.promised_date).toLocaleDateString('tr-TR') : '—'}
                      </span>
                      {promise.completed_at && (
                        <span className="text-xs text-foreground-400">
                          <i className="ri-check-line mr-0.5"></i>
                          {new Date(promise.completed_at).toLocaleDateString('tr-TR')}
                        </span>
                      )}
                    </div>
                  </div>
                  <Badge badgeClass={promiseRecordStatus.badgeClass(promise.status)} icon={promiseRecordStatus.icon(promise.status)}>{promise.status}</Badge>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Aktivite Geçmişi */}
      {selectedPromise && filteredActivities.length > 0 && (
        <div className="border-t border-background-200 pt-4">
          <h4 className="text-sm font-semibold text-foreground-800 mb-3">Görüşme Geçmişi</h4>
          <div className="space-y-2">
            {filteredActivities
              .sort((a, b) => (b.activity_date || '').localeCompare(a.activity_date || ''))
              .map((activity) => (
                <div key={activity.id} className="flex items-start gap-3 py-2 border-b border-background-100 last:border-0">
                  <span className="w-7 h-7 rounded-full bg-background-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className={`${activityTypeIcons[activity.activity_type] || 'ri-chat-3-line'} text-xs text-foreground-400`}></i>
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-medium text-foreground-700">{activityTypeLabels[activity.activity_type] || activity.activity_type}</span>
                      <span className="text-xs text-foreground-400">{activity.activity_date ? new Date(activity.activity_date).toLocaleDateString('tr-TR') : ''}</span>
                      {activity.created_by && <span className="text-xs text-foreground-400">· {activity.created_by}</span>}
                    </div>
                    {activity.note && <p className="text-xs text-foreground-500 mt-0.5">{activity.note}</p>}
                    {activity.old_promised_date && activity.new_promised_date && (
                      <p className="text-xs text-foreground-400 mt-0.5">
                        {new Date(activity.old_promised_date).toLocaleDateString('tr-TR')} → {new Date(activity.new_promised_date).toLocaleDateString('tr-TR')}
                        {activity.old_amount != null && activity.new_amount != null && ` · ${formatTRY(activity.old_amount)} → ${formatTRY(activity.new_amount)}`}
                      </p>
                    )}
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
}